/**
 * StaffChatDock — the Team chat dock (staff chat MVP, SR-14), ported from Antonin's catalog source (Signal DS
 * `catalog/src/components/TeamChat/TeamChat.tsx`, `TeamDock`; spec team-chat-mvp.md, 1 Nov 2026). Mounted once in the
 * app shell, behind `?x=staff-chat`; nothing renders with the switch off.
 *
 * Where: bottom right of every page, flush to the bottom edge, 16 from the right; 16 left of the Scrips Intelligence
 * rail or panel while one is on screen, following it (use-dock-clearance.ts). An overlay: nothing on the page moves.
 * Collapsed (256 x 48): the bar, the chat glyph, "Team", the unread total (the sum of unread messages: GET unread, kept
 * live by StaffChatUnread; the home card shows the same number), the expand chevron.
 * Open (720 x 512): the bar (+ New message, the collapse chevron), the conversations left (280, unread first), the open
 * conversation right (ThreadView), or New message, or "Choose a conversation, or start a new message.".
 * A 403 or 503 on the list fills the body ("You do not have access to Messages" / "Messages are not available right
 * now"). Escape collapses it.
 */
import { useEffect, useRef, type CSSProperties } from 'react';
import { ChevronDown, ChevronUp, MessageCircle, Plus } from 'lucide-react';
import { Button } from '@scripsteam/scrips-signal-ds';
import { isStaffChatOn, isTeamInRailOn } from '@/staff-chat/lib/staff-chat-switch';
import { useDockStore } from '@/staff-chat/lib/dock-store';
import { useDockClearance } from '@/staff-chat/lib/use-dock-clearance';
import { unreadFirst, unreadLabel } from '@/staff-chat/lib/chat-format';
import { NO_ACCESS_TITLE, staffChatErrorKind, UNAVAILABLE_TITLE } from '@/staff-chat/lib/staff-chat-errors';
import { useStaffChatIdentity, useStaffChatUnread, useStaffThreads } from '@/staff-chat/services/use-staff-chat';
import { TeamRow } from './team-row';
import { ThreadView } from './thread-view';
import { NewMessagePane } from './new-message-pane';
import { TeamCountBadge } from './team-count-badge';
import { SkeletonLines } from './team-chat-parts';

export const NOTHING_CHOSEN = 'Choose a conversation, or start a new message.';
export const THREADS_ERROR = "Couldn't load messages.";
export const NO_MESSAGES = 'No messages yet. Write to a colleague with New message.';

export function StaffChatDock() {
  if (!isStaffChatOn()) return null;
  return <TeamDock inRail={isTeamInRailOn()} />;
}

/** `inRail` (`?x=team-in-rail`): the left menu's Team tile opens the panel, so nothing is drawn while it is collapsed. */
function TeamDock({ inRail }: { inRail: boolean }) {
  const open = useDockStore((s) => s.open);
  const reset = useDockStore((s) => s.reset);
  const collapse = useDockStore((s) => s.collapse);
  const openDock = useDockStore((s) => s.openDock);
  const startNewMessage = useDockStore((s) => s.startNewMessage);
  const clearance = useDockClearance();
  const { who } = useStaffChatIdentity();
  const { data: unread = 0 } = useStaffChatUnread();

  // Another user signed in on this tab: the dock starts collapsed with nothing chosen (never the last user's thread).
  const lastWho = useRef(who);
  useEffect(() => {
    if (lastWho.current === who) return;
    lastWho.current = who;
    reset();
  }, [who, reset]);

  const toggle = () => (open ? collapse() : openDock());
  if (inRail && !open) return null;
  return (
    <div
      className="team-chat-dock"
      data-slot="staff-chat-dock"
      data-state={open ? 'open' : 'collapsed'}
      // The measured clearance from the Intelligence rail (CSS px); the stylesheet adds the 16 gap.
      style={{ '--team-chat-dock-clear': `${clearance}px` } as CSSProperties}
      onKeyDown={(e) => {
        if (open && e.key === 'Escape') {
          e.stopPropagation();
          collapse();
        }
      }}
    >
      <section className="team-chat-panel" data-open={open || undefined} aria-label="Team messages">
        <header className="team-chat-bar" data-slot="staff-chat-dock-bar">
          <button
            type="button"
            className="team-chat-bar-toggle"
            aria-expanded={open}
            aria-label={unread > 0 ? `Team, ${unreadLabel(unread)}` : 'Team'}
            onClick={toggle}
          >
            <MessageCircle aria-hidden />
            <span className="team-chat-bar-title">Team</span>
            <TeamCountBadge count={unread} />
          </button>
          {open ? (
            <Button type="button" variant="tertiary" size="sm" icon={<Plus aria-hidden />} onClick={() => startNewMessage()}>
              New message
            </Button>
          ) : null}
          <Button
            type="button"
            variant="tertiary"
            size="sm"
            icon={open ? <ChevronDown aria-hidden /> : <ChevronUp aria-hidden />}
            aria-label={open ? 'Collapse' : 'Expand'}
            onClick={toggle}
          />
        </header>
        {open ? <PanelBody /> : null}
      </section>
    </div>
  );
}

function PanelBody() {
  const threadId = useDockStore((s) => s.threadId);
  const compose = useDockStore((s) => s.compose);
  const selectThread = useDockStore((s) => s.selectThread);
  const { me } = useStaffChatIdentity();
  const threads = useStaffThreads();

  // No access (403) or not available (503): the whole body says so, no Retry.
  const kind = threads.isError && !threads.data ? staffChatErrorKind(threads.error) : null;
  if (kind === 'no-access' || kind === 'unavailable')
    return (
      <div className="team-chat-panel-body" data-single data-slot="staff-chat-dock-body">
        <p className="team-chat-note team-chat-pad">{kind === 'no-access' ? NO_ACCESS_TITLE : UNAVAILABLE_TITLE}</p>
      </div>
    );

  let list;
  if (threads.isPending)
    list = (
      <div className="team-chat-pad">
        <SkeletonLines label="Loading conversations" />
      </div>
    );
  else if (threads.isError && !threads.data)
    list = (
      <p className="team-chat-note team-chat-pad">
        {THREADS_ERROR}{' '}
        <button type="button" className="team-chat-link" onClick={() => void threads.refetch()}>
          Retry
        </button>
      </p>
    );
  else if (threads.data.items.length === 0) list = <p className="team-chat-note team-chat-pad">{NO_MESSAGES}</p>;
  else
    list = unreadFirst(threads.data.items).map((t) => (
      <TeamRow key={t.id} thread={t} me={me} selected={!compose && t.id === threadId} onClick={() => selectThread(t.id)} />
    ));

  return (
    <div className="team-chat-panel-body" data-slot="staff-chat-dock-body">
      <nav className="team-chat-panel-list" aria-label="Conversations">
        {list}
      </nav>
      <div className="team-chat-panel-thread">
        {compose ? (
          <NewMessagePane patientId={compose.patientId} />
        ) : threadId ? (
          <ThreadView key={threadId} threadId={threadId} />
        ) : (
          <p className="team-chat-note team-chat-pad">{NOTHING_CHOSEN}</p>
        )}
      </div>
    </div>
  );
}
